import jsPDF from "jspdf";
import hospitalInfo from "../constants/hospitalInfo";
import formatDate from "./formatDate";
import logoBase64 from "../constants/logoBase64";

/**
 * Generates and downloads a formal Experience Letter / Certificate PDF
 * styled according to the official Green Naturopathy Bill Template design system.
 */
export function downloadExperienceLetterPDF({
    staff = {},
    issueDate = new Date(),
    relievingDate = "",
    isCurrentlyWorking = false,
    designation = "",
    remarks = "",
    refNo = ""
}) {
    if (!staff || !staff.name) return;

    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 45;

    // Palette matching the Bill Template
    const brandGreen = [75, 128, 38];      // #4b8026 Organic Leaf Green
    const grayHeaderBg = [209, 213, 219];  // #d1d5db Header gray
    const grayContentBg = [249, 250, 251]; // #f9fafb Soft card gray
    const grayBorder = [209, 213, 219];    // #d1d5db Box border
    const textDark = [31, 41, 55];         // #1f2937 Charcoal
    const textMuted = [107, 114, 128];     // #6b7280 Muted gray
    const accentGreen = [115, 167, 66];    // #73a742 Accent green

    // ---------- Top Header: Title on Left, Logo on Right ----------
    doc.setFont("times", "bold");
    doc.setFontSize(36);
    doc.setTextColor(...brandGreen);
    doc.text("Experience Letter", marginX, 54);

    // Official Logo on top right (exact content size: 4.5cm x 2.26cm -> 127.56pt x 64.06pt)
    const logoWidth = 127.56;
    const logoHeight = 64.06;
    const logoX = pageWidth - marginX - logoWidth;
    const logoY = 18;
    if (logoBase64) {
        try {
            doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Could not render logo in Experience Letter PDF", e);
        }
    }

    let y = 92;

    // ---------- Gray-capped Reference Card ----------
    const cardWidth = pageWidth - (marginX * 2);
    const cardHeight = 36;
    const headerHeight = 14;

    doc.setFillColor(...grayContentBg);
    doc.setDrawColor(...grayBorder);
    doc.roundedRect(marginX, y, cardWidth, cardHeight, 3, 3, "FD");

    doc.setFillColor(...grayHeaderBg);
    doc.rect(marginX, y, cardWidth, headerHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(...textDark);
    doc.text("OFFICIAL CERTIFICATE REFERENCE", marginX + 10, y + 10);

    const autoRefNo = refNo || `REF: GN/EXP/${new Date(issueDate).getFullYear()}/${staff.staffId || "001"}`;
    const formattedIssueDate = formatDate(issueDate);

    doc.setFontSize(8.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("REF NO:", marginX + 10, y + 26);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(autoRefNo, marginX + 55, y + 26);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("ISSUE DATE:", pageWidth - marginX - 160, y + 26);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(formattedIssueDate, pageWidth - marginX - 90, y + 26);

    y += cardHeight + 28;

    // ---------- Certificate Titles ----------
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...brandGreen);
    doc.text("EXPERIENCE CERTIFICATE", pageWidth / 2, y, { align: "center" });
    y += 6;

    doc.setDrawColor(...accentGreen);
    doc.setLineWidth(1.5);
    doc.line((pageWidth / 2) - 85, y, (pageWidth / 2) + 85, y);
    y += 24;

    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textDark);
    doc.text("TO WHOMSOEVER IT MAY CONCERN", pageWidth / 2, y, { align: "center" });
    y += 28;

    // ---------- Letter Content ----------
    const staffName = staff.name || "Employee";
    const staffId = staff.staffId ? `(Employee ID: ${staff.staffId})` : "";
    const staffRole = designation || staff.designation || "Staff Member";
    const joinDate = staff.joiningDate ? formatDate(staff.joiningDate) : "commencement of service";

    let tenureText = "";
    if (isCurrentlyWorking) {
        tenureText = `from ${joinDate} to till date`;
    } else if (relievingDate) {
        tenureText = `from ${joinDate} to ${formatDate(relievingDate)}`;
    } else {
        tenureText = `from ${joinDate} to ${formatDate(new Date())}`;
    }

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.setLineHeightFactor(1.6);

    const p1 = `This is to certify that ${staffName} ${staffId} has been employed with Green Naturopathy ${tenureText}. During this tenure of service with us, he/she served in the capacity of ${staffRole}.`;
    const splitP1 = doc.splitTextToSize(p1, cardWidth);
    doc.text(splitP1, marginX, y);
    y += (splitP1.length * 16) + 16;

    const defaultRemarks = `During his/her tenure with our clinic, we found him/her to be sincere, dedicated, hardworking, and punctual in fulfilling assigned duties. His/Her professional knowledge, conduct, and interpersonal relationships with patients, doctors, and team members were exemplary.`;
    const conductText = remarks.trim() ? remarks.trim() : defaultRemarks;
    const splitP2 = doc.splitTextToSize(conductText, cardWidth);
    doc.text(splitP2, marginX, y);
    y += (splitP2.length * 16) + 16;

    const p3 = `He/She carries good moral character and professional ethics. We sincerely appreciate his/her valuable service and contributions to our healthcare institution, and we wish him/her grand success in all future professional endeavors.`;
    const splitP3 = doc.splitTextToSize(p3, cardWidth);
    doc.text(splitP3, marginX, y);
    y += (splitP3.length * 16) + 55;

    // Check bottom margin for signatures
    if (y + 110 > pageHeight - 50) {
        doc.addPage();
        y = 60;
    }

    // ---------- Signatures & Official Seal Space ----------
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...brandGreen);
    const centreName = hospitalInfo.centreName || "Green Naturopathy";
    doc.text(`For ${centreName}`, pageWidth - marginX - 10, y, { align: "right" });
    y += 50;

    // Official Seal placeholder
    doc.setDrawColor(...grayBorder);
    doc.setLineDashPattern([2, 2], 0);
    doc.roundedRect(marginX + 10, y - 40, 95, 48, 3, 3, "S");
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "italic");
    doc.setTextColor(...textMuted);
    doc.text("Official Hospital Seal", marginX + 18, y - 14);
    doc.setLineDashPattern([], 0); // reset dash

    // Signature line
    doc.setDrawColor(...grayBorder);
    doc.line(pageWidth - marginX - 170, y, pageWidth - marginX, y);

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textDark);
    doc.text("Authorized Signatory", pageWidth - marginX - 10, y + 14, { align: "right" });

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textMuted);
    doc.text("Medical Director / Administrator", pageWidth - marginX - 10, y + 26, { align: "right" });

    // ---------- Bottom Hospital Address Banner (Identical to Bill Template) ----------
    const footerY = pageHeight - 38;
    doc.setDrawColor(...grayBorder);
    doc.setLineWidth(0.75);
    doc.line(marginX, footerY - 10, pageWidth - marginX, footerY - 10);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(55, 65, 81);
    const fullCentreName = hospitalInfo.centreName || "GREEN NATUROPATHY";
    const addressLine = `${fullCentreName}: ${(hospitalInfo.address || "194-C, SATHY ROAD, GANAPATHY, COIMBATORE")}, ${(hospitalInfo.city || "COIMBATORE")}, ${(hospitalInfo.state || "TAMIL NADU")} ${(hospitalInfo.pincode || "641006")}`;
    doc.text(addressLine.toUpperCase(), pageWidth / 2, footerY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...textMuted);
    const phoneVal = hospitalInfo.phone ? `+91 ${hospitalInfo.phone}` : "+91 9345316319";
    const emailVal = hospitalInfo.email || "greennaturopathycbe@gmail.com";
    const webVal = hospitalInfo.websiteDisplay || "greennaturopathy.com";
    doc.text(`📞 ${phoneVal}   •   ✉️ ${emailVal}   •   🌐 ${webVal}`, pageWidth / 2, footerY + 10, { align: "center" });

    // Save File
    const safeName = staffName.replace(/[^a-zA-Z0-9]/g, "_");
    doc.save(`Experience_Certificate_${safeName}.pdf`);
}
